import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CMPS_DIALOG_DATA, CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';

import { TranslationKeyMap } from './profile.i18n';

export interface PhotoChangeEvent {
    file: File;
    url: SafeResourceUrl;
}

@Component({
    selector: 'cmps-auth-profile-change-picture',
    templateUrl: './profile-change-picture.component.html',
})
export class ProfileChangePictureComponent {
    i18n = TranslationKeyMap;
    photo: File;
    photoUrl: SafeResourceUrl;

    constructor(
        private readonly _sanitizer: DomSanitizer,
        /** @private */ readonly _dialogRef: CmpsDialogRef<
            ProfileChangePictureComponent,
            PhotoChangeEvent
        >,
        @Inject(CMPS_DIALOG_DATA)
        private readonly _data: {
            initialPhotoUrl: SafeResourceUrl;
            photo: File;
        },
    ) {
        this.photo = _data.photo;
    }

    onPhotoChanged($event): void {
        if (!$event.target.files.length) {
            this.photo = null;
            this.photoUrl = this._data.initialPhotoUrl;
        } else {
            this.photo = $event.target.files[0];
            this.photoUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
                URL.createObjectURL(this.photo),
            );
        }
    }

    /** @private */
    _onAcceptClick(): void {
        this._dialogRef.close({ file: this.photo, url: this.photoUrl });
    }
}

@Component({
    selector: 'cmps-auth-profile-picture-btn',
    template: ` <div class="cmps-profile-picture-change">
        <div
            tabindex="0"
            role="img"
            [attr.aria-labelledby]="ariaLabelledBy"
            [attr.aria-label]="ariaLabel"
        >
            <div class="cmps-profile-picture">
                <img
                    [src]="photoUrl"
                    *ngIf="hasPhotoUrl"
                />
                <cmps-icon
                    class="cmps-cdk-transform-scale-8"
                    svgIcon="account-circle"
                    *ngIf="!hasPhotoUrl"
                ></cmps-icon>
            </div>
            <cmps-wait-status
                [absolute]="true"
                *ngIf="hasPendingPhotoUrl"
            ></cmps-wait-status>
        </div>
        <button
            type="button"
            class="btn-banner"
            (click)="showChangePictureModal()"
            *ngIf="isEditable && !hasPendingPhotoUrl && !hasPhotoUrl"
        >
            <cmps-icon svgIcon="plus-circle"></cmps-icon>
            {{ i18n._694_ADD_A_PHOTO | translate }}
        </button>
        <button
            type="button"
            class="btn-banner"
            (click)="showChangePictureModal()"
            *ngIf="isEditable && !hasPendingPhotoUrl && hasPhotoUrl"
        >
            <cmps-icon svgIcon="camera"></cmps-icon>
            {{ i18n._695_CHANGE_PHOTO | translate }}
        </button>
    </div>`,
    exportAs: 'cmpsProfilePictureBtn',
})
@TakeUntilDestroy()
export class ProfilePictureBtn implements OnChanges {
    @Input('editable') isEditable: boolean;
    @Input() photoUrl: SafeResourceUrl | Promise<SafeResourceUrl>;
    @Input() ariaLabelledBy: string;
    @Input() ariaLabel: string;

    @Output() photoUrlChange: EventEmitter<SafeResourceUrl>;
    @Output() photoChange: EventEmitter<PhotoChangeEvent>;

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const change = changes['photoUrl'];
        if (change && change.currentValue instanceof Promise) {
            this.hasPendingPhotoUrl = true;
            change.currentValue.then(
                (photoUrl) => {
                    this.photoUrl = photoUrl;
                    this.photoUrlChange.emit(this.photoUrl);
                    this.hasPendingPhotoUrl = false;
                },
                () => {
                    this.photoUrl = null;
                    this.photoUrlChange.emit(this.photoUrl);
                    this.hasPendingPhotoUrl = false;
                },
            );
        } else {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            this.photoUrl = changes['photoUrl'].currentValue;
        }
    }

    i18n = TranslationKeyMap;
    hasPendingPhotoUrl: boolean;

    get hasPhotoUrl(): boolean {
        return (
            this.photoUrl &&
            typeof this.photoUrl === 'object' &&
            !(this.photoUrl instanceof Promise)
        );
    }

    constructor(private readonly _dialog: CmpsDialog) {
        this.isEditable = false;
        this.photoUrlChange = new EventEmitter<SafeResourceUrl>();
        this.photoChange = new EventEmitter<PhotoChangeEvent>();
    }

    showChangePictureModal(): void {
        if (!this.isEditable || this.hasPendingPhotoUrl) return;

        const ref = this._dialog.open<
            ProfileChangePictureComponent,
            any,
            PhotoChangeEvent
        >(ProfileChangePictureComponent, {
            data: {
                initialPhotoUrl: !this.hasPhotoUrl ? null : this.photoUrl,
                photoUrl: !this.hasPhotoUrl ? null : this.photoUrl,
            },
        });
        ref.result
            .pipe(
                tap((result) => {
                    if (result?.file != null) {
                        this.photoUrl = result.url;
                        this.photoUrlChange.emit(this.photoUrl);
                        this.photoChange.emit(result);
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
